import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { PLANS } from '../../config/plans';

const ctaLabel = 'Voir les plans';
const PaywallModal = ({
  isOpen,
  onClose,
  feature = 'details',
  onSubscribe,
  theme // "dark" | "light"
}) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const featureConfig = {
    details: {
      icon: 'BarChart3',
      title: 'Accédez aux détails complets',
      description:
        "Débloquez les graphiques détaillés, les projections avancées et l'analyse complète de cette propriété.",
      cta: 'Débloquer les détails'
    },
    scenarios: {
      icon: 'GitCompare',
      title: 'Comparez des scénarios',
      description:
        'Créez plusieurs scénarios de financement et comparez-les pour trouver la meilleure option.',
      cta: 'Activer la comparaison'
    },
    new_analysis: {
      icon: 'PlusCircle',
      title: 'Analysez plus de propriétés',
      description:
        'Vous avez utilisé vos 3 analyses gratuites. Passez au plan Analyse ou Pro pour créer une nouvelle analyse.',
      cta: 'Analyser plus de propriétés'
    }
  };

  const config = featureConfig[feature] || featureConfig.details;

  const handleSubscribe = () => {
    if (onSubscribe) onSubscribe();
    else navigate('/subscription-pricing');
    onClose();
  };

  // Overlay adaptatif
  const overlayBg = theme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.35)';

  // Tokens (avec fallbacks)
  const popover =
    theme === 'dark'
      ? 'var(--color-popover, #0b0f14)'
      : 'var(--color-popover, #ffffff)';

  const popoverFg =
    theme === 'dark'
      ? 'var(--color-popover-foreground, var(--color-foreground, #f3f4f6))'
      : 'var(--color-popover-foreground, var(--color-foreground, #111827))';

  const mutedFg =
    theme === 'dark'
      ? 'var(--color-muted-foreground, rgba(243,244,246,0.72))'
      : 'var(--color-muted-foreground, rgba(17,24,39,0.70))';

  const muted =
    theme === 'dark'
      ? 'var(--color-muted, rgba(255,255,255,0.06))'
      : 'var(--color-muted, rgba(17,24,39,0.05))';

  const border =
    theme === 'dark'
      ? 'var(--color-border, rgba(255,255,255,0.12))'
      : 'var(--color-border, rgba(17,24,39,0.12))';

  const primary =
    theme === 'dark'
      ? 'var(--color-primary, #3b82f6)'
      : 'var(--color-primary, #2563eb)';

  const success =
    theme === 'dark'
      ? 'var(--color-success, #22c55e)'
      : 'var(--color-success, #16a34a)';

  return (
    <div
      className={theme === 'dark' ? 'dark' : ''}
      style={{
        position: 'fixed',
        inset: 0,
        background: overlayBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--spacing-lg, 20px)',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: popover,
          color: popoverFg,
          border: `1px solid ${border}`,
          borderRadius: 'var(--radius-xl, 18px)',
          padding: 'var(--spacing-xl, 28px)',
          maxWidth: 520,
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          boxShadow:
            theme === 'dark'
              ? '0 20px 60px rgba(0,0,0,0.45), 0 2px 10px rgba(0,0,0,0.25)'
              : '0 20px 60px rgba(0,0,0,0.18), 0 2px 10px rgba(0,0,0,0.10)'
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="rounded-md transition-colors"
          style={{
            position: 'absolute',
            top: 'var(--spacing-md, 12px)',
            right: 'var(--spacing-md, 12px)',
            background: 'transparent',
            border: `1px solid ${border}`,
            cursor: 'pointer',
            color: mutedFg,
            padding: 8,
            lineHeight: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = muted;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Icon name="X" size={18} />
        </button>

        {/* ✅ Enlevé: le rond avec le + */}

        <h3
          style={{
            fontSize: '1.25rem',
            fontWeight: 800,
            margin: '0 0 var(--spacing-sm, 10px)',
            color: popoverFg
          }}
        >
          {config.title}
        </h3>

        <p
          style={{
            fontSize: '0.95rem',
            color: mutedFg,
            margin: '0 0 var(--spacing-xl, 28px)',
            lineHeight: 1.65
          }}
        >
          {config.description}
        </p>

        {/* Pricing card */}
        <div
          style={{
            background: muted,
            borderRadius: 'var(--radius-lg, 14px)',
            padding: 'var(--spacing-lg, 18px)',
            marginBottom: 'var(--spacing-lg, 20px)',
            border: `1px solid ${border}`
          }}
        >
          <p style={{ fontSize: '0.75rem', color: mutedFg, margin: '0 0 6px' }}>
            À partir de
          </p>
          <p style={{ fontSize: '1.6rem', fontWeight: 850, color: popoverFg, margin: 0 }}>
            {PLANS.analysis.priceLabel}
          </p>
          <p style={{ fontSize: '0.8rem', color: success, margin: '8px 0 0' }}>
            Pro disponible à {PLANS.pro.priceLabel}; codes promo saisis dans Stripe Checkout.
          </p>
        </div>

        {/* CTA principal (vrai bouton + hover + outline) */}
        <Button
  variant="default"
  size="lg"
  onClick={handleSubscribe}
  className="w-full h-11 rounded-md px-8 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 inline-flex items-center justify-center"
>
  {ctaLabel}
  <Icon name="ArrowRight" size={18} className="ml-2" />
</Button>


        {/* CTA secondaire (ghost, mais cliquable évident) */}
        <Button
          variant="ghost"
          size="lg"
          onClick={onClose}
          className="transition-all"
          style={{
            width: '100%',
            border: `1px solid ${border}`,
            background: 'transparent',
            color: mutedFg
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = muted;
            e.currentTarget.style.color = popoverFg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = mutedFg;
          }}
        >
          Pas maintenant
        </Button>

        {/* Petit détail UX: mettre le focus visible */}
        <style>{`
          button:focus-visible {
            outline: 2px solid ${primary};
            outline-offset: 2px;
          }
        `}</style>
      </div>
    </div>
  );
};

export default PaywallModal;
